import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { getShareLinkAudit, type ShareLinkAuditRow, type AuditFilters } from "@/utils/admin-audit.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/audit")({
  component: AdminAuditPage,
});

const PAGE_SIZE = 25;
const OWNER_EMAIL = "jenny@adkavi.com";

function outcomeVariant(outcome: string): "default" | "secondary" | "destructive" | "outline" {
  if (outcome === "success") return "default";
  if (outcome === "duplicate") return "secondary";
  if (outcome === "rate_limited" || outcome === "ip_rate_limited" || outcome === "honeypot" || outcome === "captcha_failed") return "destructive";
  return "outline";
}

function toIsoOrNull(localDate: string, endOfDay = false): string | null {
  if (!localDate) return null;
  const d = new Date(localDate);
  if (Number.isNaN(d.getTime())) return null;
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function formatCsvCell(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function downloadCsv(rows: ShareLinkAuditRow[]) {
  const headers = ["Timestamp (ISO)", "Email", "IP", "IP Requests 24h", "Outcome", "Link ID", "Original Path", "User Agent"];
  const lines = [headers.map(formatCsvCell).join(",")];
  for (const r of rows) {
    lines.push(
      [
        new Date(r.created_at).toISOString(),
        r.requester_email,
        r.ip_address ?? "",
        r.ip_requests_24h,
        r.outcome,
        r.link_id ?? "",
        r.original_path ?? "",
        r.user_agent ?? "",
      ].map(formatCsvCell).join(",")
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `share-link-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AdminAuditPage() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [outcome, setOutcome] = useState<AuditFilters["outcome"]>("all");
  const [emailSearch, setEmailSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<ShareLinkAuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);
  const [exporting, setExporting] = useState(false);

  const isOwner = (user?.email ?? "").toLowerCase() === OWNER_EMAIL;

  // Client-side fast guard. Server still enforces owner-only on the function.
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/auth" }); return; }
    if (!isOwner) { navigate({ to: "/dashboard" }); }
  }, [loading, user, isOwner, navigate]);

  const filters = useMemo<AuditFilters>(() => ({
    outcome,
    emailSearch: emailSearch.trim() || null,
    fromIso: toIsoOrNull(fromDate),
    toIso: toIsoOrNull(toDate, true),
    page,
    pageSize: PAGE_SIZE,
    fetchAll: false,
  }), [outcome, emailSearch, fromDate, toDate, page]);

  useEffect(() => {
    if (loading || !isOwner) return;
    setFetching(true);
    setError(null);
    getShareLinkAudit({ data: filters })
      .then((r) => { setRows(r.rows); setTotal(r.total); })
      .catch((e) => setError(e?.message ?? "Failed to load"))
      .finally(() => setFetching(false));
  }, [filters, loading, isOwner]);

  // Reset to page 1 when filters change (except page itself)
  useEffect(() => { setPage(1); }, [outcome, emailSearch, fromDate, toDate]);

  async function handleExport() {
    setExporting(true);
    try {
      const r = await getShareLinkAudit({
        data: { ...filters, page: 1, pageSize: PAGE_SIZE, fetchAll: true },
      });
      if (r.rows.length === 0) {
        toast.info(t("admin.toast.noRowsExport"));
        return;
      }
      downloadCsv(r.rows);
      toast.success(t("admin.toast.exportedRows", { n: r.rows.length }));
    } catch (e) {
      toast.error((e as Error)?.message ?? t("admin.toast.exportFailed"));
    } finally {
      setExporting(false);
    }
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!isOwner) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-semibold mb-2">Forbidden</h1>
        <p className="text-muted-foreground">This page is restricted to the app owner.</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Link2 className="w-8 h-8 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">Share Link Audit</span>
          </h1>
          <p className="text-muted-foreground">All share-link request attempts.</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label htmlFor="f-outcome" className="text-xs">Outcome</Label>
              <Select value={outcome} onValueChange={(v) => setOutcome(v as AuditFilters["outcome"])}>
                <SelectTrigger id="f-outcome" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="allowed">Allowed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="bot">Bot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="f-email" className="text-xs">Email contains</Label>
              <Input
                id="f-email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="user@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="f-from" className="text-xs">From date</Label>
              <Input id="f-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="f-to" className="text-xs">To date</Label>
              <Input id="f-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            {fetching ? "Loading…" : `${total.toLocaleString()} ${total === 1 ? "entry" : "entries"}`}
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {error && <div className="text-destructive text-sm mb-3">{error}</div>}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>IP</TableHead>
                <TableHead className="text-right">IP req / 24h</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Link ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isBlocked = ["rate_limited", "ip_rate_limited", "honeypot", "captcha_failed"].includes(row.outcome);
                const overLimit = row.ip_requests_24h >= 10;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-medium break-all">{row.requester_email}</TableCell>
                    <TableCell className="font-mono text-xs">{row.ip_address ?? "—"}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${isBlocked && overLimit ? "text-destructive font-semibold" : ""}`}>
                      {row.ip_address ? row.ip_requests_24h : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={outcomeVariant(row.outcome)}>{row.outcome}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs break-all max-w-xs">
                      {row.link_id ?? "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!fetching && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No entries match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Showing {rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}
              –{(page - 1) * PAGE_SIZE + rows.length} of {total.toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || fetching}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || fetching}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
